import React from "react";
import { useAssistance, useDrag, useView } from "./hooks";
import { PluginContent, PluginHeader } from "./components/common/layout";
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Grid,
  GridItem,
  HStack,
} from "@chakra-ui/react";
import VehicleCard from "./components/vehicle/VehicleCard";
import LocationCard from "./components/location/LocationCard";
import CameraCard from "./components/cameras/CameraCard";
import ChatCard from "./components/chat/ChatCard";
import { BreakdownProvider } from "./providers";
import { PluginGridLayout } from "./constants";
import { AssistanceCategory, PluginView } from "./enums";
import { AlertContainer } from "./components/common/alerts";
import { useAuth } from "../hooks";
import OccupantsCard from "./components/occupants/OccupantsCard";
import PlayerCard from "./components/player/PlayerCard";
import SettingsCard from "./components/settings/SettingsCard";

/**
 * Renders the plugin
 * 
 * @returns The plugin
 */
const Plugin = () => {
  const { isDragging, onDragStart } = useDrag();
  const { pluginView, isChatOpen, isSettingsOpen } = useView();
  const { user } = useAuth();
  const { assistance } = useAssistance();

  if (!user) {
    return null;
  }

  return (
    <Card variant={pluginView !== PluginView.COMPACT ? "opened" : "closed"}
      borderRadius={"20px"}
      borderStyle={"solid"}>
      <CardHeader
        cursor={isDragging ? "grabbing" : "grab"}
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
        borderRadius={pluginView === PluginView.COMPACT && "15px"}
      >
        <PluginHeader />
      </CardHeader>
      <CardBody padding={0} paddingTop={"16px"}>
        <PluginContent>
          <AlertContainer />
          <HStack gap={"16px"} alignItems={"stretch"}>
            <Grid
              templateColumns={PluginGridLayout.TemplateColumns[pluginView]}
              templateRows={PluginGridLayout.TemplateRows[pluginView]}
              gap={"16px"}
              flexGrow={1}
              maxWidth={pluginView === PluginView.STANDARD ? "400px" : pluginView === PluginView.LARGE ? "700px" : "1200px"}
            >
              <GridItem
                colSpan={PluginGridLayout.VehicleCard.ColSpan[pluginView]}
                rowSpan={PluginGridLayout.VehicleCard.RowSpan[pluginView]}
              >
                <BreakdownProvider>
                  <VehicleCard />
                </BreakdownProvider>
              </GridItem>
              <GridItem
                colSpan={PluginGridLayout.LocationCard.ColSpan[pluginView]}
                rowSpan={PluginGridLayout.LocationCard.RowSpan[pluginView]}
              >
                <LocationCard />
              </GridItem>
              {pluginView === PluginView.FULL && (
                <GridItem
                  colSpan={PluginGridLayout.ChatCard.ColSpan[pluginView]}
                  rowSpan={PluginGridLayout.ChatCard.RowSpan[pluginView]}
                >
                  {assistance?.category !== AssistanceCategory.ECALL ? (
                    <ChatCard />
                  ) : (
                    <OccupantsCard />
                  )}
                </GridItem>
              )}
              {(assistance?.category === AssistanceCategory.ECALL && (pluginView === PluginView.LARGE || pluginView === PluginView.FULL)) && (
                <GridItem
                  colSpan={PluginGridLayout.CameraCard.ColSpan[pluginView] / (pluginView === PluginView.FULL ? 2 : 1)}
                  rowSpan={PluginGridLayout.CameraCard.RowSpan[pluginView]}
                >
                  <PlayerCard />
                </GridItem>
              )}
              {((assistance?.category !== AssistanceCategory.ECALL && pluginView === PluginView.LARGE) || pluginView === PluginView.FULL) && (
                <GridItem
                  colSpan={PluginGridLayout.CameraCard.ColSpan[pluginView] / (assistance?.category === AssistanceCategory.ECALL ? 2 : 1)}
                  rowSpan={PluginGridLayout.CameraCard.RowSpan[pluginView]}
                >
                  <CameraCard />
                </GridItem>
              )}
            </Grid>
            {assistance?.category !== AssistanceCategory.ECALL && pluginView !== PluginView.FULL && isChatOpen && (
              <Box flexGrow={1}>
                <ChatCard />
              </Box>
            )}
            {isSettingsOpen && (
              <Box flexGrow={1}>
                <SettingsCard />
              </Box>
            )}
          </HStack>
        </PluginContent>
      </CardBody>
    </Card >
  );
};

export default React.memo(Plugin);
